import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, inject, NgZone, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { ArticlesService } from '../../../services/articles.service';
import { ApiResponse } from '../../../interfaces/api-response';
import { ArticleArtisticGeneral } from '../../../interfaces/article-artistic';
import { CardArchiveComponent } from '../components/card-archive/card-archive.component';
import { ArchiveSearchStateService } from '../../../services/archive-search-state.service';

@Component({
  selector: 'app-archive-artistic',
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    CardArchiveComponent,
  ],
  templateUrl: './archive-artistic.component.html',
  styleUrl: './archive-artistic.component.scss',
})
export class ArchiveArtisticComponent implements OnDestroy {

  readonly pageSizeOptions = [1, 12, 20, 40, 60];

  totalResults = 0;
  totalPages = 1;
  itemsPerPage = 20;
  currentPageIndex = 1;

  filters: string | null = null;
  articles: ArticleArtisticGeneral[] = [];

  filterForm = new FormGroup({
    title: new FormControl<string | null>(null),
  });

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private readonly archiveSearchState = inject(ArchiveSearchStateService);
  private pagerObserver?: ResizeObserver;
  private compactPager = false;

  constructor(
    public articlesService: ArticlesService,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  navigate(id: number): void {
    this.persistState();
    this.router.navigate(['scheda', id], { relativeTo: this.route });
  }

  ngOnInit(): void {
    const textFromQuery = this.route.snapshot.queryParamMap.get('text');
    const saved = this.archiveSearchState.getArtistic();

    if (textFromQuery) {
      this.filters = textFromQuery;
      this.filterForm.get('title')?.setValue(textFromQuery);
      this.currentPageIndex = 1;
      this.persistState();
      this.clearQueryParams();
      this.fetchArticles(1);
      return;
    }

    if (saved) {
      this.filters = saved.query;
      this.itemsPerPage = saved.itemsPerPage;
      this.currentPageIndex = saved.currentPageIndex;
      this.filterForm.get('title')?.setValue(saved.query);
      this.fetchArticles(saved.currentPageIndex);
      return;
    }

    this.fetchArticles(1);
  }

  ngAfterViewInit(): void {
    const nav = this.host.nativeElement.querySelector('.archive-pages');
    if (!(nav instanceof HTMLElement)) {
      return;
    }
    this.pagerObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      const compact = width < 640;
      if (compact === this.compactPager) {
        return;
      }
      this.zone.run(() => {
        this.compactPager = compact;
        this.scrollActivePageIntoView();
      });
    });
    this.pagerObserver.observe(nav);
  }

  ngOnDestroy(): void {
    this.pagerObserver?.disconnect();
  }

  get hasActiveSearch(): boolean {
    return !!this.filters?.trim();
  }

  searchArticles(): void {
    const query = this.filterForm.get('title')?.value?.trim() ?? '';
    this.filters = query || null;
    this.currentPageIndex = 1;
    this.persistState();
    this.fetchArticles(1);
    this.scrollToTopOfPage();
  }

  resetSearch(): void {
    this.filterForm.reset({ title: null });
    this.filters = null;
    this.currentPageIndex = 1;
    this.archiveSearchState.clearArtistic();
    this.fetchArticles(1);
    this.scrollToTopOfPage();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPageIndex) {
      return;
    }
    this.currentPageIndex = page;
    this.persistState();
    this.fetchArticles(page);
    this.scrollToTopOfPage();
  }

  setItemsPerPage(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!this.pageSizeOptions.includes(value) || value === this.itemsPerPage) {
      return;
    }
    this.itemsPerPage = value;
    this.currentPageIndex = 1;
    this.persistState();
    this.fetchArticles(1);
    this.scrollToTopOfPage();
  }

  get pageTokens(): Array<number | 'ellipsis'> {
    const total = Math.max(this.totalPages, 1);
    const current = this.currentPageIndex;
    if (this.compactPager) {
      const start = Math.max(1, current - 1);
      const end = Math.min(total, current + 1);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    }
    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }
    const tokens: Array<number | 'ellipsis'> = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) {
      tokens.push('ellipsis');
    }
    for (let page = start; page <= end; page++) {
      tokens.push(page);
    }
    if (end < total - 1) {
      tokens.push('ellipsis');
    }
    tokens.push(total);
    return tokens;
  }

  private fetchArticles(page: number): void {
    this.articlesService.getArticlesArtisticFromServer(this.itemsPerPage, page, this.filters ?? '').
      subscribe((val: ApiResponse<{ listArticles: ArticleArtisticGeneral[], totalPages: number, totalResults: number }>) => {
        this.articles = val.data.listArticles;
        this.totalPages = val.data.totalPages || 1;
        this.totalResults = val.data.totalResults;
        this.currentPageIndex = page;
        this.persistState();
        this.scrollActivePageIntoView();
      });
  }

  private persistState(): void {
    this.archiveSearchState.setArtistic({
      query: this.filters,
      itemsPerPage: this.itemsPerPage,
      currentPageIndex: this.currentPageIndex,
    });
  }

  private clearQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  private scrollToTopOfPage(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onPageListScroll(event: Event): void {
    const list = event.target;
    if (list instanceof HTMLElement) {
      this.updatePageListFade(list);
    }
  }

  private scrollActivePageIntoView(): void {
    setTimeout(() => {
      this.host.nativeElement.querySelectorAll('.page-list').forEach((list: Element) => {
        if (!(list instanceof HTMLElement)) {
          return;
        }
        const active = list.querySelector('.is-active');
        if (active instanceof HTMLElement) {
          const left = active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2;
          list.scrollTo({ left: Math.max(0, left) });
        }
        this.updatePageListFade(list);
      });
    });
  }

  private updatePageListFade(list: HTMLElement): void {
    const maxScroll = list.scrollWidth - list.clientWidth;
    const canScrollLeft = maxScroll > 1 && list.scrollLeft > 1;
    const canScrollRight = maxScroll > 1 && list.scrollLeft < maxScroll - 1;
    list.classList.toggle('can-scroll-left', canScrollLeft);
    list.classList.toggle('can-scroll-right', canScrollRight);
  }

}
